import { useEffect, useRef, useState } from "react";
import { Trash2 } from "lucide-react";

/**
 * Mobile-only swipe-to-action row.
 *
 * - Children render as-is on desktop (`enabled={false}`) — the host screen keeps its usual layout.
 * - On touch devices the content slides left to reveal an action button on the right.
 *   The row only resolves once the swipe passes a threshold; vertical scrolling takes priority so
 *   finger-drags that go up/down never translate the row.
 * - A swipe does NOT fire the action; tapping the revealed button is the only destructive action.
 * - Defaults keep the original delete behaviour (red trash) — pass `actionIcon` / `actionClassName`
 *   / `deleteLabel` to repurpose it for other row-level actions (e.g. archive).
 */
export default function SwipeToDeleteRow({ enabled = false, onDelete, children, testId, deleteLabel = "Remove from basket", actionIcon, actionClassName }) {
  const ACTION_WIDTH = 80;             // visible width of the red delete button
  const OPEN_SNAP = 72;                // >= this many px to the left → stay open
  const DIRECTION_LOCK = 8;            // px of movement before deciding horizontal vs vertical

  const [offset, setOffset] = useState(0);
  const [animating, setAnimating] = useState(true);
  const openRef = useRef(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const baseOffset = useRef(0);
  const direction = useRef(null);      // null | "h" | "v"
  const containerRef = useRef(null);

  // Close the row when disabled toggles (e.g. viewport changes to desktop) or when children change identity.
  useEffect(() => { setOffset(0); openRef.current = false; }, [enabled]);

  // Close on outside tap so only one row can be open at a time per screen touch.
  useEffect(() => {
    if (!enabled) return undefined;
    const handler = (e) => {
      if (!openRef.current) return;
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setAnimating(true);
        setOffset(0);
        openRef.current = false;
      }
    };
    document.addEventListener("touchstart", handler, { passive: true });
    return () => document.removeEventListener("touchstart", handler);
  }, [enabled]);

  if (!enabled) {
    return <div data-testid={testId}>{children}</div>;
  }

  const onTouchStart = (e) => {
    const t = e.touches[0];
    startX.current = t.clientX;
    startY.current = t.clientY;
    baseOffset.current = openRef.current ? -ACTION_WIDTH : 0;
    direction.current = null;
    setAnimating(false);
  };

  const onTouchMove = (e) => {
    const t = e.touches[0];
    const dx = t.clientX - startX.current;
    const dy = t.clientY - startY.current;
    if (direction.current === null) {
      if (Math.abs(dx) < DIRECTION_LOCK && Math.abs(dy) < DIRECTION_LOCK) return;
      direction.current = Math.abs(dx) > Math.abs(dy) ? "h" : "v";
    }
    if (direction.current === "v") return;      // let the page scroll vertically
    // Clamp between 0 (closed) and -(ACTION_WIDTH + small overscroll) with light resistance beyond.
    let next = baseOffset.current + dx;
    if (next > 0) next = next * 0.3;             // right-pull resistance when already closed
    if (next < -ACTION_WIDTH) next = -ACTION_WIDTH + (next + ACTION_WIDTH) * 0.3;
    setOffset(next);
  };

  const onTouchEnd = () => {
    setAnimating(true);
    if (direction.current === "h") {
      const shouldOpen = offset <= -OPEN_SNAP;
      openRef.current = shouldOpen;
      setOffset(shouldOpen ? -ACTION_WIDTH : 0);
    } else {
      // No horizontal intent → snap back to whatever state we started in.
      setOffset(baseOffset.current);
    }
    direction.current = null;
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    openRef.current = false;
    setAnimating(true);
    setOffset(0);
    onDelete?.();
  };

  return (
    <div ref={containerRef} className="relative overflow-hidden touch-pan-y" data-testid={testId}>
      <button
        type="button"
        onClick={handleDelete}
        aria-label={deleteLabel}
        className={`absolute inset-y-0 right-0 w-20 text-white grid place-items-center focus:outline-none focus-visible:ring-2 ${actionClassName || "bg-rose-600 active:bg-rose-700 focus-visible:ring-rose-300"}`}
        data-testid={`${testId}-delete`}
        tabIndex={offset <= -OPEN_SNAP ? 0 : -1}
      >
        {actionIcon || <Trash2 className="h-5 w-5" aria-hidden="true" />}
      </button>
      <div
        className="relative bg-white"
        style={{ transform: `translate3d(${offset}px, 0, 0)`, transition: animating ? "transform 220ms cubic-bezier(0.22, 1, 0.36, 1)" : "none", willChange: "transform" }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
      >
        {children}
      </div>
    </div>
  );
}
